import { FormEvent, useEffect, useState } from 'react';
import { IconId } from '@tabler/icons';

import { userService } from '#root/services';
import { AddressDocumentFields } from '#root/components';
import type { AddressDocumentValue } from '#root/components/AddressDocumentFields';
import {
  Button,
  Modal,
  ModalBody,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '#root/components/ui';
import { AddressDTO, addressReceiverName } from '#root/models/Address';
import { DocumentType, DocumentTypeValue } from '#root/models/Shipping';
import { isValidDocNumber } from '#root/utils';

const FORM_ID = 'address-document-form';

interface DialogAddressDocumentProps {
  open: boolean;
  address: AddressDTO | null;
  onClose: () => void;
  onSuccess: () => void;
  createMessage: {
    success: (msg: string) => void;
    error: (msg: string) => void;
  };
}

/** Completa el documento de quien recibe en una dirección que todavía no lo tiene. */
function DialogAddressDocument(props: DialogAddressDocumentProps) {
  const { open, address, onClose, onSuccess, createMessage } = props;
  const [docType, setDocType] = useState<DocumentTypeValue>(DocumentType.dni);
  const [docNumber, setDocNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isFormValid = isValidDocNumber(docType, docNumber);
  const receiverName = addressReceiverName(address);

  const handleDocumentChange = (value: AddressDocumentValue) => {
    setDocType(value.docType);
    setDocNumber(value.docNumber);
  };

  const handleConfirm = async () => {
    if (!address || !isFormValid || submitting) return;
    setSubmitting(true);
    const res = await userService.setAddressDocument(address.id, docType, docNumber);
    setSubmitting(false);
    if (res.error) {
      // El diálogo sigue abierto para corregir o cancelar
      createMessage.error(res.message || 'No se pudo guardar el documento.');
      return;
    }
    createMessage.success(res.message || 'Documento guardado en la dirección.');
    onSuccess();
    onClose();
  };

  // Enter en el número de documento guarda, igual que el botón
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleConfirm();
  };

  // Mientras guarda no se cierra
  const handleClose = () => {
    if (!submitting) onClose();
  };

  useEffect(() => {
    if (open) {
      setDocType(DocumentType.dni);
      setDocNumber('');
    }
  }, [open]);

  return (
    <Modal open={open} onClose={handleClose} disableBackdropClose>
      <ModalHeader icon={<IconId />}>
        <ModalTitle>Documento de quien recibe</ModalTitle>
        {address && (
          <ModalDescription>
            {receiverName && <strong>{receiverName} · </strong>}
            {address.address_1}, {address.district}
          </ModalDescription>
        )}
      </ModalHeader>
      <ModalBody>
        <form id={FORM_ID} onSubmit={handleSubmit}>
          <AddressDocumentFields
            docType={docType}
            docNumber={docNumber}
            onChange={handleDocumentChange}
            disabled={submitting}
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button type="submit" form={FORM_ID} loading={submitting} disabled={!isFormValid}>
          Guardar
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default DialogAddressDocument;
